import { z } from 'zod';

/**
 * Invitaciones que emite el residente desde su app.
 *
 * La vivienda **no está aquí a propósito**: se resuelve del residente
 * autenticado. Si viajara en la petición, cualquiera con sesión podría emitir
 * invitaciones a la casa de otro cambiando un id.
 */

/** "HH:MM" en 24 h. Se acepta con o sin cero delante: "9:00" y "09:00". */
const horaSchema = z
  .string()
  .trim()
  .regex(/^([01]?\d|2[0-3]):[0-5]\d$/, 'La hora va en formato HH:MM');

export const crearInvitacionSchema = z
  .object({
    /**
     * **Obligatorio.** El guardia lo lee en voz alta cuando el visitante se
     * para en la reja, y es lo que convierte un código en una invitación a
     * alguien: sin nombre es un pase al portador que se reenvía por WhatsApp y
     * en la caseta no hay con qué contrastar quién está ahí enfrente.
     *
     * Se exige **al emitir**, y la columna sigue admitiendo nulos a propósito:
     * las invitaciones anteriores a esta regla existen y el historial del
     * residente tiene que poder enseñarlas sin inventarles un nombre.
     *
     * **Sesenta y no ciento veinte, y el número sale de una cuenta.** Este
     * nombre viaja dentro del token firmado, y el token tiene un techo. Con 120
     * caracteres el token se pasaba —basta ASCII para llegar a 521— y la reja lo
     * rechazaba por largo antes de mirar la firma, con el visitante ya parado
     * ahí. Lo peor era que dependía del condominio: con un slug corto pasaba y
     * con uno largo no.
     *
     * Se prohíben los caracteres de control, que nadie teclea y `JSON.stringify`
     * escapa a seis bytes cada uno: ciento veinte de ellos inflaban el token a
     * 1321. Se rechazan en vez de limpiarse porque quien los manda no es un
     * residente escribiendo un nombre.
     */
    nombreInvitado: z
      .string()
      .trim()
      .min(1, 'El nombre de la visita es obligatorio.')
      .max(60)
      .regex(/^[^\p{C}]*$/u, 'El nombre lleva caracteres que no se pueden usar.'),
    telefono: z.string().trim().max(30).optional(),

    /** Si se omite, vale desde este momento. */
    vigenteDesde: z.string().datetime().optional(),

    /**
     * Hasta cuándo sirve. **Con tope de un año**, que antes no tenía.
     *
     * Sin tope se podía emitir un QR válido hasta 2040, y eso importa por dónde
     * vive la confianza: el QR se verifica **solo con su firma**, sin consultar
     * nada. Un pase que no caduca es una llave del fraccionamiento que se
     * regaló, y que solo se puede quitar cancelándola una por una —cosa que hoy
     * ni siquiera surte efecto en la reja hasta que exista la lista de
     * revocados—.
     *
     * Un año y no menos porque las recurrentes existen para la trabajadora
     * doméstica que viene cada martes, y renovarle el pase cada trimestre es
     * justo la fricción que hace que un residente deje de usar el sistema.
     */
    vigenteHasta: z
      .string()
      .datetime()
      .refine(
        (iso) => Date.parse(iso) <= Date.now() + 366 * 24 * 60 * 60 * 1000,
        'Una invitación no puede valer más de un año.',
      ),

    /**
     * Cuántas veces sirve. Uno por omisión: la invitación de un solo uso es la
     * que no se puede reenviar por WhatsApp a media colonia.
     */
    usosMaximos: z.number().int().min(1).max(50).default(1),

    /**
     * Invitación recurrente: solo estos días de la semana (0=domingo) y solo
     * dentro de esta franja horaria. Sirve para la persona que viene cada
     * semana sin emitirle un código nuevo cada vez.
     *
     * Viajan **dentro del token firmado**, así que la reja los verifica sin
     * consultar al servidor; no son un adorno de la pantalla.
     */
    diasSemana: z.array(z.number().int().min(0).max(6)).max(7).optional(),
    horaInicio: horaSchema.optional(),
    horaFin: horaSchema.optional(),
  })
  .refine(
    (v) => {
      if (!v.horaInicio || !v.horaFin) return true;
      const min = (h: string) => {
        const [hh, mm] = h.split(':');
        return Number(hh) * 60 + Number(mm);
      };
      return min(v.horaFin) > min(v.horaInicio);
    },
    { message: 'La hora de fin tiene que ser posterior a la de inicio', path: ['horaFin'] },
  );
export type CrearInvitacion = z.infer<typeof crearInvitacionSchema>;

export interface InvitacionEmitida {
  id: string;
  /** El token firmado que va dentro del QR. Es lo único que el visitante necesita. */
  token: string;
  unidad: string;
  /** Nunca nulo al emitir: el esquema lo exige desde que el cliente lo pidió. */
  nombreInvitado: string;
  /** Lo necesita el texto que se comparte con el visitante. */
  creadoEn: string;
  /**
   * Los límites de velocidad del fraccionamiento, que ese mismo texto
   * recuerda.
   *
   * Viajan con cada invitación —y no se piden aparte— porque el mensaje se
   * arma tanto con una recién emitida como con una sacada del historial, y así
   * las dos traen todo lo que necesitan. Son los vigentes hoy, no los del día
   * en que se emitió: lo que le importa a quien va a manejar es la regla que
   * está en pie cuando entre.
   */
  limiteVelocidadCamellon: number;
  limiteVelocidadCalles: number;
  vigenteDesde: string;
  vigenteHasta: string;
  usosMaximos: number;
  diasSemana: number[];
  horaInicio: string | null;
  horaFin: string | null;
}

/**
 * Cómo quiere el residente que le avisen.
 *
 * `LLAMADA` no está: existe en el schema para la cascada del guardia, pero un
 * residente no elige que le marquen por cada visita.
 */
export const preferenciasAvisoSchema = z.object({
  canalAviso: z.enum(['PUSH', 'WHATSAPP']).optional(),
  recibeAvisos: z.boolean().optional(),
});
export type PreferenciasAviso = z.infer<typeof preferenciasAvisoSchema>;

/**
 * El aparato del residente dándose de alta para recibir avisos.
 *
 * El token lo emite Firebase, no nosotros, y cambia solo: al reinstalar la
 * app, al restaurar un respaldo o cuando a Google le parece. Por eso la app lo
 * reenvía en cada arranque y el alta es idempotente — no es un registro que se
 * hace una vez, es un «sigo siendo yo, y este es mi número de hoy».
 *
 * El límite de 4096 es holgura sobre los ~160 caracteres que mide hoy: acota
 * lo que puede entrar a la base sin apostar a un formato que no controlamos.
 */
export const registrarAparatoSchema = z.object({
  token: z.string().min(10).max(4096),
  plataforma: z.enum(['android', 'ios']),
});
export type RegistrarAparato = z.infer<typeof registrarAparatoSchema>;
